import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { Seo } from "./seo";
import type { SeoProps } from "./seo";

/* ─── Imagery (Warmwind-style: alpine golden hour + warm offices) ─────────── */
export const IMG = {
  // Serene alpine peaks at golden hour reflected in a lake
  hero: "https://images.unsplash.com/photo-1454496522488-7a8e488e8606?auto=format&fit=crop&w=2400&q=80",
  mist: "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=1600&q=80",
  lake: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=1600&q=80",
  // Diverse young professionals collaborating in a warm modern office
  team: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1600&q=80",
  team2: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=1200&q=80",
  office: "https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&w=1200&q=80",
  desk: "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?auto=format&fit=crop&w=1200&q=80",
};

/* ─── Layout primitives ───────────────────────────────────────────────────── */
export function Container({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <div className={`mx-auto w-full max-w-6xl px-6 ${className}`}>{children}</div>;
}

export function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-warm-border bg-warm-card px-3 py-1 text-xs font-medium uppercase tracking-[0.14em] text-warm-muted">
      <span className="h-1.5 w-1.5 rounded-full bg-warm-accent" />
      {children}
    </span>
  );
}

/** Standard centered page header for secondary marketing pages. */
export function PageHeader({
  eyebrow,
  title,
  accent,
  tail,
  sub,
  children,
}: {
  eyebrow: string;
  title: string;
  accent?: string;
  tail?: string;
  sub: string;
  children?: ReactNode;
}) {
  return (
    <section className="relative overflow-hidden">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-80"
        style={{ background: "radial-gradient(60% 60% at 50% 0%, rgba(244,201,155,0.5) 0%, rgba(244,201,155,0) 70%)" }}
      />
      <Container className="relative pt-20 pb-12 text-center sm:pt-28">
        <Eyebrow>{eyebrow}</Eyebrow>
        <h1 className="mx-auto mt-7 max-w-3xl text-balance text-5xl font-bold leading-[1.05] tracking-tight sm:text-6xl">
          {title}
          {accent && <span className="font-display italic font-normal text-warm-accent"> {accent}</span>}
          {tail && ` ${tail}`}
        </h1>
        <p className="mx-auto mt-6 max-w-2xl text-lg text-warm-muted">{sub}</p>
        {children && <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">{children}</div>}
      </Container>
    </section>
  );
}

/* ─── Buttons / links ─────────────────────────────────────────────────────── */
type BtnVariant = "primary" | "dark" | "outline" | "ghost";
const BTN_BASE =
  "inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors";
const BTN_STYLES: Record<BtnVariant, string> = {
  primary: "bg-warm-accent text-white hover:bg-warm-accentDark shadow-sm shadow-warm-accent/20",
  dark: "bg-warm-ink text-warm-bg hover:bg-black",
  outline: "border border-warm-border bg-warm-card text-warm-ink hover:border-warm-ink/30",
  ghost: "text-warm-ink/80 hover:text-warm-ink",
};

export function MButton({
  children,
  to,
  href,
  variant = "primary",
  className = "",
}: {
  children: ReactNode;
  to?: string;
  href?: string;
  variant?: BtnVariant;
  className?: string;
}) {
  const cls = `${BTN_BASE} ${BTN_STYLES[variant]} ${className}`;
  if (href) {
    return (
      <a href={href} className={cls}>
        {children}
      </a>
    );
  }
  return (
    <Link to={to ?? "#"} className={cls}>
      {children}
    </Link>
  );
}

export function ArrowRight({ className = "" }: { className?: string }) {
  return (
    <svg className={`h-4 w-4 ${className}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M5 12h14M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* ─── Brand mark — a warm sun cresting a horizon ──────────────────────────── */
export function Logo({ className = "" }: { className?: string }) {
  return (
    <span className={`relative inline-flex h-8 w-8 items-center justify-center overflow-hidden rounded-xl bg-warm-ink ${className}`}>
      <svg viewBox="0 0 32 32" className="h-8 w-8" aria-hidden>
        <defs>
          <linearGradient id="sun" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#F6C98A" />
            <stop offset="100%" stopColor="#DE7A45" />
          </linearGradient>
        </defs>
        <circle cx="16" cy="18" r="7" fill="url(#sun)" />
        <path d="M0 23 L11 16 L17 20 L24 14 L32 20 L32 32 L0 32 Z" fill="#34302a" />
        <path d="M0 26 L9 21 L16 24 L23 19 L32 24 L32 32 L0 32 Z" fill="#241F19" />
      </svg>
    </span>
  );
}

export function Wordmark() {
  return (
    <span className="text-[15px] font-semibold tracking-tight text-warm-ink">
      Wonderful<span className="text-warm-accent">Interview</span>
    </span>
  );
}

/* ─── Navigation ──────────────────────────────────────────────────────────── */
const NAV_LINKS: [string, string][] = [
  ["/platform", "Platform"],
  ["/pricing", "Pricing"],
  ["/security", "Security"],
  ["/careers", "Careers"],
  ["/blog", "Blog"],
];

function Nav() {
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  // Close the mobile menu whenever the route changes.
  useEffect(() => {
    setOpen(false);
  }, [loc.pathname]);

  return (
    <header className="sticky top-0 z-50 border-b border-warm-border/70 bg-warm-bg/85 backdrop-blur-md">
      <Container className="flex h-16 items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" aria-label="WonderfulInterview home">
          <Logo />
          <Wordmark />
        </Link>

        <nav className="hidden items-center gap-1 md:flex" aria-label="Primary">
          {NAV_LINKS.map(([to, label]) => {
            const active = loc.pathname === to;
            return (
              <Link
                key={to}
                to={to}
                className={`rounded-full px-3.5 py-2 text-sm font-medium transition-colors ${
                  active ? "text-warm-ink" : "text-warm-muted hover:text-warm-ink"
                }`}
              >
                {label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/login"
            className="hidden rounded-full px-4 py-2 text-sm font-medium text-warm-ink/80 hover:text-warm-ink sm:inline-flex"
          >
            Sign in
          </Link>
          <MButton to="/signup" variant="dark" className="hidden sm:inline-flex">
            Get started
          </MButton>
          {/* Mobile menu toggle */}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-warm-border bg-warm-card text-warm-ink md:hidden"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
              {open ? (
                <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
              ) : (
                <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />
              )}
            </svg>
          </button>
        </div>
      </Container>

      {/* Mobile dropdown menu */}
      {open && (
        <div className="border-t border-warm-border bg-warm-bg md:hidden">
          <Container className="flex flex-col py-4">
            {NAV_LINKS.map(([to, label]) => (
              <Link key={to} to={to} className="rounded-lg px-2 py-3 text-base font-medium text-warm-ink hover:bg-warm-sand">
                {label}
              </Link>
            ))}
            <div className="mt-3 flex flex-col gap-2 border-t border-warm-border pt-4">
              <MButton to="/login" variant="outline">
                Sign in
              </MButton>
              <MButton to="/signup" variant="dark">
                Get started
              </MButton>
            </div>
          </Container>
        </div>
      )}
    </header>
  );
}

/* ─── Footer ──────────────────────────────────────────────────────────────── */
const FOOTER_COLS: { title: string; links: [string, string][] }[] = [
  {
    title: "Product",
    links: [
      ["/platform", "Platform"],
      ["/pricing", "Pricing"],
      ["/security", "Security"],
      ["/signup", "Get started"],
    ],
  },
  {
    title: "Company",
    links: [
      ["/about", "About"],
      ["/careers", "Careers"],
      ["/blog", "Blog"],
      ["/contact", "Contact"],
    ],
  },
  {
    title: "Legal & account",
    links: [
      ["/privacy", "Privacy"],
      ["/terms", "Terms"],
      ["/login", "Sign in"],
      ["/app", "Dashboard"],
    ],
  },
];

function Footer() {
  return (
    <footer className="bg-warm-ink text-warm-bg">
      <Container className="py-16">
        <div className="grid gap-12 md:grid-cols-[1.4fr_1fr_1fr_1fr]">
          <div className="max-w-xs">
            <div className="flex items-center gap-2.5">
              <Logo />
              <span className="text-[15px] font-semibold tracking-tight">
                Wonderful<span className="text-warm-glow">Interview</span>
              </span>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-warm-bg/60">
              Rigorous AI interviews that schedule, conduct, proctor, and score themselves — so your team only
              meets the people worth meeting.
            </p>
          </div>
          {FOOTER_COLS.map((col) => (
            <div key={col.title}>
              <div className="text-xs font-semibold uppercase tracking-[0.14em] text-warm-bg/40">{col.title}</div>
              <ul className="mt-4 space-y-2.5">
                {col.links.map(([to, label], i) => (
                  <li key={`${to}-${i}`}>
                    {to.startsWith("/") ? (
                      <Link to={to} className="text-sm text-warm-bg/70 transition-colors hover:text-warm-bg">
                        {label}
                      </Link>
                    ) : (
                      <a href={to} className="text-sm text-warm-bg/70 transition-colors hover:text-warm-bg">
                        {label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-14 flex flex-col items-start justify-between gap-4 border-t border-warm-bg/10 pt-6 text-sm text-warm-bg/50 sm:flex-row sm:items-center">
          <span>© {2026} WonderfulInterview · a Wonderful Agent product</span>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="hover:text-warm-bg">Privacy</Link>
            <Link to="/terms" className="hover:text-warm-bg">Terms</Link>
            <Link to="/security" className="hover:text-warm-bg">Security</Link>
          </div>
        </div>
      </Container>
    </footer>
  );
}

/* ─── Page shell ──────────────────────────────────────────────────────────── */
export function MarketingPage({ seo, children }: { seo: SeoProps; children: ReactNode }) {
  // Scroll to top on route change for SPA navigation.
  const loc = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [loc.pathname]);

  return (
    <div className="min-h-screen bg-warm-bg font-sans text-warm-ink antialiased selection:bg-warm-accent/25">
      <Seo {...seo} />
      <Nav />
      <main>{children}</main>
      <Footer />
    </div>
  );
}
